/**
 * ToastContainer - Custom toast renderer for Chakra UI
 *
 * Drop-in replacement for Chakra UI's useToast hook with Dashboard styling.
 * Mobile: full-width top stack. Desktop: top-right.
 */

"use client";

import {
  createStandaloneToast,
  useBreakpointValue,
  useToast as useChakraToast,
  type ToastPosition,
  type UseToastOptions,
} from "@chakra-ui/react";
import { useCallback, type ReactNode } from "react";
import { Toast } from "./Toast";

export interface CustomToastOptions extends Omit<UseToastOptions, "render"> {
  title?: ReactNode;
  description?: ReactNode;
  status?: "success" | "error" | "warning" | "info" | "loading";
  icon?: ReactNode;
}

function getDefaultToastLayout(isMobile: boolean | undefined) {
  if (isMobile) {
    return {
      position: "top" as ToastPosition,
      containerStyle: {
        marginTop: "12px",
        width: "100%",
        maxWidth: "100%",
        paddingLeft: "16px",
        paddingRight: "16px",
        alignItems: "stretch" as const,
      },
    };
  }

  return {
    position: "top-right" as ToastPosition,
    containerStyle: {
      marginTop: "16px",
      marginRight: "16px",
      maxWidth: "400px",
    },
  };
}

export function useToast() {
  const chakraToast = useChakraToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const layout = getDefaultToastLayout(isMobile);

  const toastFn = useCallback(
    (options?: CustomToastOptions) => {
      if (!options) {
        return chakraToast(options);
      }

      const {
        title,
        description,
        status = "info",
        isClosable = true,
        duration = 5000,
        icon,
        position = layout.position,
        containerStyle,
        ...restOptions
      } = options;

      return chakraToast({
        position,
        duration,
        isClosable,
        containerStyle: containerStyle ?? layout.containerStyle,
        ...restOptions,
        render: ({ onClose }) => (
          <Toast
            title={title}
            description={description}
            status={status}
            isClosable={isClosable}
            onClose={onClose}
            icon={icon}
            duration={duration ?? undefined}
          />
        ),
      });
    },
    [chakraToast, layout.containerStyle, layout.position],
  );

  return Object.assign(toastFn, {
    update: chakraToast.update,
    promise: chakraToast.promise,
    closeAll: chakraToast.closeAll,
    close: chakraToast.close,
    isActive: chakraToast.isActive,
  });
}

export const toast = (() => {
  const { toast: standaloneToast } = createStandaloneToast();

  return (options: CustomToastOptions) => {
    const {
      title,
      description,
      status = "info",
      isClosable = true,
      duration = 5000,
      icon,
      position = "top-right",
      ...restOptions
    } = options;

    standaloneToast({
      position,
      duration,
      isClosable,
      ...restOptions,
      render: ({ onClose }) => (
        <Toast
          title={title}
          description={description}
          status={status}
          isClosable={isClosable}
          onClose={onClose}
          icon={icon}
          duration={duration ?? undefined}
        />
      ),
    });
  };
})();
